/* eslint-disable no-prototype-builtins */
import React from 'react';
import uuid from 'uuid/v1';

import {
  Button,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Table,
  TextField,
  Tooltip
} from '@mui/material';

import { isEmpty, isEqual, filter, find, map } from 'lodash';
import TableContent from '../TableContent';

/**

 * Button element.
 *
 * @param {object} arg - The object argument.
 * @param {any} arg.key - The key of element.
 * @param {string} arg.id - The id of element.
 * @param {Function} arg.onClick  - The function on click element.
 * @param {any} arg.contentButton - The content of element.
 * @param {boolean} arg.disabled - Is disabled or not element.
 * @param {string} arg.size - The size of element.
 * @param {string} arg.color - The size of element.
 * @param {string} arg.variant - The size of element.
 * @param {object} arg.style - The style of element.
 * @param {object} arg.moreProps - The more props to element.
 * @returns {Element} The element of Material MUI library.
 * @throws Error if something is wrong.
 */
export const renderButton = ({
  key = 'button',
  id = 'button',
  onClick = () => {},
  contentButton = '',
  disabled = false,
  size = 'small',
  color = 'primary',
  variant = 'outlined',
  style = {
    color: '#2A3F90',
    borderColor: '#2A3F90'
  },
  moreProps = {}
}) => {
  const propsElement = {
    key,
    id,
    onClick,
    disabled,
    size,
    color,
    variant,
    style,
    ...moreProps
  };

  return <Button {...propsElement}>{contentButton}</Button>;
};

/**
 * CheckBox element.
 *
 * @param {object} arg - The object argument.
 * @param {any} arg.key - The key of element.
 * @param {string} arg.id - The id of element.
 * @param {boolean} arg.disabled - Function to change value selector.
 * @param {string} arg.normalName - The value of element.
 * @param {Function} arg.onChange - Function to change value selector.
 * @param {string} arg.size - The size of element.
 * @param {object} arg.style - The style of element.
 * @param {string} arg.value - The value of element.
 * @param {object} arg.moreProps - The more props to element.
 * @returns {Element} The element of Material MUI library.
 * @throws Error if something is wrong.
 */
export const renderCheckBox = ({
  key = 'checkBox',
  id = 'checkBox',
  disabled = false,
  onChange,
  value,
  normalName = value,
  size = 'small',
  style = {
    color: '#2A3F90',
    '&.Mui-checked': {
      color: '#2A3F90'
    }
  },
  moreProps = {}
}) => {
  const propsElement = {
    key,
    id,
    checked: value,
    onChange,
    size,
    sx: style,
    value: 'variable',
    disabled,
    ...moreProps
  };
  return (
    <>
      <Checkbox {...propsElement} />
      {normalName}
    </>
  );
};

/**

 * Chip element.
 *
 * @param {object} arg - The object argument.
 * @param {Function} arg.onChange  - The function on change element.
 * @param {Array} arg.arrayOptions - The options to display.
 * @param {boolean} arg.disabled - Is disabled or not element.
 * @param {string} arg.size - The size of element.
 * @param {Array} arg.value - The size of element.
 * @returns {Element} The element of Material MUI library.
 * @throws Error if something is wrong.
 */
export const renderChip = ({
  onChange,
  label,
  arrayOptions,
  value,
  disabled = false,
  size = 'small'
}) => {
  return (
    <Box display="row" fullWidth mb={2}>
      <FormControl fullWidth>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          labelId="demo-multiple-chip-label"
          size={size}
          multiple
          disabled={disabled}
          value={isEmpty(value) ? [] : value}
          onChange={onChange}
          input={<OutlinedInput id="select" label={label} />}
          renderValue={selected => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map(item => (
                <Chip key={item} label={item} />
              ))}
            </Box>
          )}
        >
          {arrayOptions.map(item => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

/**

 * IconButton element.
 *
 * @param {object} arg - The object argument.
 * @param {any} arg.key - The key of element.
 * @param {string} arg.id - The id of element.
 * @param {Function} arg.onClick  - The function on click element.
 * @param {Element} arg.icon - The icon to button.
 * @param {string} arg.titleTooltip - The title of element.
 * @param {boolean} arg.disabled - Is disabled or not element.
 * @param {string} arg.size - The size of element.
 * @param {object} arg.style - The style of element.
 * @param {object} arg.moreProps - The more props to element.
 * @returns {Element} The element of Material MUI library.
 * @throws Error if something is wrong.
 */
export const renderIconButton = ({
  key = 'iconButton',
  id = 'iconButton',
  onClick = () => {},
  icon,
  titleTooltip = '',
  disabled = false,
  size = 'small',
  style = { color: '#2A3F90' },
  moreProps = {}
}) => {
  const propsElement = {
    key,
    id,
    size,
    style,
    disabled,
    onClick,
    ...moreProps
  };
  return (
    <Tooltip key={uuid()} title={titleTooltip} placement="top-start">
      <IconButton {...propsElement}>{icon}</IconButton>
    </Tooltip>
  );
};

/**

 * RadioButton element.
 *
 * @param {object} arg - The object argument.
 * @param {any} arg.key - The key of element.
 * @param {string} arg.id - The id of element.
 * @param {boolean} arg.isRow - Is row or column radio button.
 * @param {string} arg.size - Size of element.
 * @param {object} arg.style - Is row or column radio checkBox.
 * @param {Array} arg.arrayOptions - Array with options values string.
 * @param {Function} arg.onChange - Fucntion to get value on change.
 * @param {string} arg.valueChecked - Value to check option.
 * @param {boolean} arg.showLabel - Is show text aside radio button.
 * @param {Array} arg.arrayLabels - More elements to show in label.
 * @param {boolean} arg.isArrayObject - Is show text aside radio button.
 * @param {object} arg.moreProps - The more props to element.
 * @returns {Element} The element of Material MUI library.
 * @throws Error if something is wrong.
 */

export const renderRadioButton = ({
  key = 'radioButton',
  id = 'radioButton',
  isRow = true,
  size = 'small',
  style = {},
  arrayOptions = [],
  onChange = () => {},
  valueChecked = '',
  arrayLabels = map(arrayOptions, () => ''),
  showLabel = true,
  isArrayObject = false,
  moreProps = {}
}) => {
  return (
    <RadioGroup key={key} id={id} row={isRow} style={style}>
      {arrayOptions.map((option, index) => (
        <FormControlLabel
          key={uuid()}
          value={!isArrayObject ? option : option.value}
          control={
            <Radio
              key={`${!isArrayObject ? option : option.value}-${uuid()}`}
              size={size}
              onChange={() => onChange(option, index)}
              checked={isEqual(!isArrayObject ? option : option.uniqueValue, valueChecked)}
              {...moreProps}
            />
          }
          label={
            showLabel ? (
              <>
                {!isArrayObject ? option : option.value} {arrayLabels[index]}
              </>
            ) : (
              ''
            )
          }
        />
      ))}
    </RadioGroup>
  );
};

/**
 * Select element.
 *
 * @param {object} arg - The object argument.
 * @param {any} arg.key - The key of element.
 * @param {string} arg.id - The value select of element.
 * @param {boolean} arg.native - The more props to element.
 * @param {boolean} arg.multiple - The more props to element.
 * @param {string} arg.label - The label of element.
 * @param {string} arg.inputLabel - The input label of element.
 * @param {string} arg.size - The size of element.
 * @param {Function} arg.onChange - Function to change value selector.
 * @param {string} arg.value - Value selected.
 * @param {Array} arg.arrayOptions - Array with items to menu options.
 * @param {object} arg.style - The style of element.
 * @param {object} arg.moreProps - The more props to element.
 * @returns {Element} The element of Material MUI library.
 * @throws Error if something is wrong.
 */
export const renderSelect = ({
  key = 'select',
  id = 'select',
  native = true,
  multiple = false,
  label = '',
  inputLabel = '',
  size = 'small',
  onChange = () => {},
  value = '',
  arrayOptions = [],
  style = {},
  moreProps = {}
}) => {
  const propsElement = {
    key,
    id,
    native,
    multiple,
    labelId: `selector-${inputLabel}`,
    label,
    size,
    style,
    onChange: event => onChange(event),
    value,
    ...moreProps
  };
  return (
    <FormControl key={uuid()} style={{ width: '100%' }}>
      <InputLabel
        key={uuid()}
        id={`selector-${inputLabel}`}
        shrink
        htmlFor={`selector-${inputLabel}`}
      >
        {inputLabel}
      </InputLabel>
      <Select {...propsElement}>
        {arrayOptions.map(optionValue => (
          <option key={optionValue} value={optionValue}>
            {optionValue}
          </option>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * Switch element.
 *
 * @param {object} arg - The object argument.
 * @param {any} arg.key - The key of element.
 * @param {string} arg.id - The value select of element.
 * @param {boolean} arg.checked - The enable or disabled element.
 * @param {Function} arg.onChange - Function to change value selector.
 * @param {object} arg.style - The style of element.
 * @param {object} arg.styleLabel - The style of element form control label.
 * @param {object} arg.moreProps - The more props to element.
 * @param {string} arg.labelCheck - The label of element when checked is true.
 * @param {string} arg.labelUncheck - The label of element when checked is false.
 * @returns {Element} The element of Material MUI library.
 * @throws Error if something is wrong.
 */

export const renderSwitch = ({
  key = 'switch',
  id = 'switch',
  checked = false,
  onChange = () => {},
  disabled = false,
  styleLabel = {},
  style = {
    '& .MuiSwitch-switchBase': {
      color: '#2A3F90',
      '&.Mui-checked': {
        color: '#2A3F90'
      },
      '&.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#2A3F90',
        border: '2px solid #2A3F90'
      }
    },
    '& .MuiSwitch-thumb': {
      border: '2px solid #2A3F90'
    },
    '& .MuiSwitch-track': {
      backgroundColor: '#ffffff',
      border: '2px solid #2A3F90',
      opacity: 1
    }
  },
  moreProps = {},
  labelCheck = '',
  labelUncheck = ''
}) => {
  const propsElement = {
    key,
    id,
    checked,
    disabled,
    onChange: event => onChange(event),
    style: { style },
    ...moreProps
  };
  return (
    <FormGroup key={uuid()}>
      <FormControlLabel
        key={uuid()}
        control={<Switch {...propsElement} />}
        label={checked ? labelCheck : labelUncheck}
        style={styleLabel}
      />
    </FormGroup>
  );
};

/**
 * Custom table generic.
 *
 * @param {[object]} table - The table elements.
 * @param {number} table.row - Row number.
 * @param {number} table.column - Column umber.
 * @param {string} table.rowJustifyContent - The justify content of row.
 * @param {string} table.rowWidth - The width of row.
 * @param {string} table.colWidth - The width of col.
 * @param {string} table.maxWidthCol  - The max width of column.
 * @param {string} table.mbRow - The margin bottom of row.
 * @param {string} table.mtRow - The margin top of row.
 * @param {string} table.mlRow - The margin left of row.
 * @param {string} table.mrRow - The margin right of row.
 * @param {string} table.mbCol - The margin bottom of column.
 * @param {string} table.mtCol - The margin top of column.
 * @param {string} table.mrCol - The margin right of column.
 * @param {string} table.mlCol - The margin left of.
 * @param {boolean} table.isShowRow - Is show row.
 * @param {boolean} table.isShowCol - Is show column.
 * @param {boolean} table.startPosRow - Position of element in row with columns.
 * @param {boolean} table.showElement - Is show element.
 * @param {Array} table.elements - Array with elements to render.
 * @returns {Element} The element of Material MUI library.
 * @throws Error if something is wrong.
 */
export const renderTable = table => {
  const constructorTableSchema = (rows = 0) => {
    const objectRow = {
      indexRow: -1,
      rowWidth: '100%',
      mbRow: '0%',
      mtRow: '0%',
      mlRow: '0%',
      mrRow: '0%',
      isShowRow: true,
      elementsRenderRow: [],
      rowColumns: []
    };
    const arrayRows = [];
    for (let indexRow = 0; indexRow < rows; indexRow += 1)
      arrayRows.push({ ...objectRow, indexRow });
    return { arrayRows };
  };

  const totalRows = Math.max(...map(table, element => element.row));
  const { arrayRows } = constructorTableSchema(totalRows);

  const elementsToRender = map(arrayRows, row => {
    const elementsRow = filter(table, item => isEqual(item.row, row.indexRow + 1));
    const firstElement = find(elementsRow, col =>
      isEqual(col.column, Math.min(...map(elementsRow, colRow => colRow.column)))
    );
    const totalRowEl = filter(
      elementsRow,
      element => isEqual(element.column, 0) || element.column < 0
    );
    const totalColumnsEl = filter(
      elementsRow,
      element => !isEqual(element.column, 0) && element.column > 0
    );

    const newColumns = [];
    map(totalColumnsEl, totalColEl => {
      const existColumn = find(newColumns, newCol =>
        isEqual(newCol.indexCol + 1, totalColEl.column)
      );
      if (isEmpty(existColumn)) {
        const newObject = {
          colWidth: totalColEl.hasOwnProperty('colWidth') ? totalColEl.colWidth : '100%',
          maxWidthCol: totalColEl.hasOwnProperty('maxWidthCol') ? totalColEl.maxWidthCol : '100%',
          mbCol: totalColEl.hasOwnProperty('mbCol') ? totalColEl.mbCol : '0%',
          mtCol: totalColEl.hasOwnProperty('mtCol') ? totalColEl.mtCol : '0%',
          mrCol: totalColEl.hasOwnProperty('mrCol') ? totalColEl.mrCol : '0%',
          mlCol: totalColEl.hasOwnProperty('mlCol') ? totalColEl.mlCol : '0%',
          indexCol: totalColEl.column - 1,
          isShowCol: totalColEl.hasOwnProperty('isShowCol') ? totalColEl.isShowCol : true,
          elementsRenderCol: map(totalColEl.elements, el => {
            return {
              isShowElCol: totalColEl.showElement,
              elementCol: el
            };
          })
        };
        newColumns.push(newObject);
      } else {
        map(totalColEl.elements, el => {
          const newElement = {
            isShowElCol: totalColEl.showElement,
            elementCol: el
          };
          existColumn.elementsRenderCol.push(newElement);
        });

        newColumns.splice(existColumn.indexCol, 1, existColumn);
      }
    });

    const elementsRenderRow = [];
    map(totalRowEl, totalRow => {
      map(totalRow.elements, el => {
        const objectElRow = {
          isShowElRow: totalRow.showElement,
          startPos: totalRow.hasOwnProperty('startPosRow') ? totalRow.startPosRow : true,
          elementRow: el
        };
        elementsRenderRow.push(objectElRow);
      });
    });
    return {
      ...row,
      rowWidth: firstElement.hasOwnProperty('rowWidth') ? firstElement.rowWidth : '100%',
      mbRow: firstElement.hasOwnProperty('mbRow') ? firstElement.mbRow : '0%',
      mtRow: firstElement.hasOwnProperty('mtRow') ? firstElement.mtRow : '0%',
      mlRow: firstElement.hasOwnProperty('mlRow') ? firstElement.mlRow : '0%',
      mrRow: firstElement.hasOwnProperty('mrRow') ? firstElement.mrRow : '0%',
      rowJustifyContent: firstElement.hasOwnProperty('rowJustifyContent')
        ? firstElement.rowJustifyContent
        : 'space-around',
      elementsRenderRow,
      isShowRow: firstElement.hasOwnProperty('isShowRow') ? firstElement.isShowRow : true,
      rowColumns: newColumns
    };
  });

  if (isEmpty(elementsToRender))
    return (
      <Table size="small" aria-label="a dense table">
        <TableContent isTableEmpty numberOfColumns={1} itemName="content, table empty" />
      </Table>
    );
  return map(
    elementsToRender,
    (row, indexRow) =>
      row.isShowRow && (
        <Stack
          key={`row-${indexRow}`}
          id={`row-${indexRow}`}
          width={row.rowWidth}
          marginTop={row.mtRow}
          marginBottom={row.mbRow}
          marginLeft={row.mlRow}
          marginRight={row.mrRow}
          direction="row"
          display="flex"
          justifyContent={row.rowJustifyContent}
        >
          {map(
            row.elementsRenderRow,
            itemRow => itemRow.isShowElRow && itemRow.startPos && itemRow.elementRow
          )}
          {map(
            row.rowColumns,
            (col, indexCol) =>
              col.isShowCol && (
                <Stack
                  key={`column-${indexRow}${indexCol}`}
                  id={`column-${indexRow}${indexCol}`}
                  width={col.colWidth}
                  marginBottom={col.mbCol}
                  marginTop={col.mtCol}
                  marginRight={col.mrCol}
                  marginLeft={col.mlCol}
                  maxWidth={col.maxWidthCol}
                  direction="column"
                >
                  {map(col.elementsRenderCol, itemCol => itemCol.isShowElCol && itemCol.elementCol)}
                </Stack>
              )
          )}
          {map(
            row.elementsRenderRow,
            itemRow => itemRow.isShowElRow && !itemRow.startPos && itemRow.elementRow
          )}
        </Stack>
      )
  );
};

/**
 * Textfield element.
 *
 * @param {object} arg - The object argument.
 * @param {any} arg.key - The key of element.
 * @param {string} arg.id - The id of element.
 * @param {string} arg.type - The type of element.
 * @param {string} arg.label - The label of element.
 * @param {string} arg.size - The size of element.
 * @param {object} arg.style - The style of element.
 * @param {object} arg.moreProps - The more props to element.
 * @returns {Element} The element of Material MUI library.
 * @throws Error if something is wrong.
 */
export const renderTextField = ({
  key = 'textField',
  id = 'textfield',
  type = 'text',
  label = '',
  size = 'small',
  style = {},
  moreProps = {}
}) => {
  const propsElement = {
    key,
    id,
    type,
    label,
    size,
    style,
    ...moreProps
  };
  return <TextField {...propsElement} />;
};
